import React, { memo, useCallback, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { generateDataTestidAttribute } from 'ui-components/services/testid-service';
import { SplitShipRowDataPropType } from '../../prop-types';
import { Button } from 'ui-components/components/button';
import { NumberInput } from 'ui-components/components/number-input';
import ItemRowContent from '../columns/item-row-content';
import { useSplitShipModalContext } from '../../split-ship-modal-context-provider';
import { SPLIT_SHIP_ACTIONS, useTrackSplitShipAction } from '../../event-tracking/use-track-split-ship-action';
import messages from './messages';
import style from '../items-table-row.module.less';

const MIN_SPLIT_ROW_QUANTITY = 1;

const SplitRow = ({ rowData }) => {
  const { applySplit, createNewFulfillmentPlan, setSplitRowId } = useSplitShipModalContext();

  const [childQuantity, setChildQuantity] = useState(1);
  const [parentFulfillmentPlanId, setParentFulfillmentPlanId] = useState(rowData.fulfillmentPlanId);
  const [childFulfillmentPlanId, setChildFulfillmentPlanId] = useState(undefined);

  const maxSplitQuantity = rowData.quantity - MIN_SPLIT_ROW_QUANTITY;
  const parentQuantity = rowData.quantity - childQuantity;
  const isApplyDisabled =
    childQuantity < MIN_SPLIT_ROW_QUANTITY || parentQuantity < MIN_SPLIT_ROW_QUANTITY || parentFulfillmentPlanId === childFulfillmentPlanId;

  const updateParentQuantity = useCallback(
    (updatedValue) => {
      const updatedNumberValue = updatedValue || 0;
      setChildQuantity(rowData.quantity - updatedNumberValue);
    },
    [rowData.quantity],
  );

  const updateChildQuantity = useCallback((updatedValue) => {
    const updatedNumberValue = updatedValue || 0;
    setChildQuantity(updatedNumberValue);
  }, []);

  const trackCancelSplit = useTrackSplitShipAction(SPLIT_SHIP_ACTIONS.CANCEL_LINE_ITEM_SPLIT);
  const trackApplySplit = useTrackSplitShipAction(SPLIT_SHIP_ACTIONS.APPLY_LINE_ITEM_SPLIT);

  const close = useCallback(() => {
    setSplitRowId('');
    setChildFulfillmentPlanId(undefined);
  }, [setSplitRowId]);

  const cancel = useCallback(() => {
    close();
    trackCancelSplit();
  }, [close, trackCancelSplit]);

  const apply = useCallback(() => {
    let newChildFulfillmentPlanId = childFulfillmentPlanId;
    if (!newChildFulfillmentPlanId) {
      const newFulfillmentPlan = createNewFulfillmentPlan();
      newChildFulfillmentPlanId = newFulfillmentPlan.fulfillmentPlanId;
    }

    let newParentFulfillmentPlanId = parentFulfillmentPlanId;
    if (!newParentFulfillmentPlanId) {
      const newFulfillmentPlan = createNewFulfillmentPlan();
      newParentFulfillmentPlanId = newFulfillmentPlan.fulfillmentPlanId;
    }

    applySplit(rowData.dataRowId, parentQuantity, newParentFulfillmentPlanId, childQuantity, newChildFulfillmentPlanId);

    close();
    trackApplySplit();
  }, [
    applySplit,
    childFulfillmentPlanId,
    childQuantity,
    close,
    createNewFulfillmentPlan,
    parentFulfillmentPlanId,
    parentQuantity,
    rowData.dataRowId,
    trackApplySplit,
  ]);

  return (
    <>
      <tr className={classnames(style.row, style.highlighted)} {...generateDataTestidAttribute('split-row-parent')}>
        <ItemRowContent
          actionsComponent={
            <div className={style['button-container']}>
              <Button onClick={cancel} theme='link'>
                <FormattedMessage {...messages.cancelButtonText} />
              </Button>
              <div className={style.divider} />
              <Button
                disabled={isApplyDisabled}
                disabledTooltipMessage={<FormattedMessage {...messages.saveDisabledTooltip} />}
                onClick={apply}
                theme='link'
              >
                <FormattedMessage {...messages.applyButtonText} />
              </Button>
            </div>
          }
          fulfillmentPlanId={parentFulfillmentPlanId}
          name={rowData.name}
          onSelectFulfillmentPlan={setParentFulfillmentPlanId}
          productId={rowData.productId}
          quantityComponent={
            <NumberInput max={maxSplitQuantity} min={MIN_SPLIT_ROW_QUANTITY} onChange={updateParentQuantity} value={parentQuantity} />
          }
          sku={rowData.sku}
        />
      </tr>
      <tr className={classnames(style.row, style.highlighted)} {...generateDataTestidAttribute('split-row-child')}>
        <ItemRowContent
          fulfillmentPlanId={childFulfillmentPlanId}
          name={rowData.name}
          onSelectFulfillmentPlan={setChildFulfillmentPlanId}
          productId={rowData.productId}
          quantityComponent={
            <NumberInput max={maxSplitQuantity} min={MIN_SPLIT_ROW_QUANTITY} onChange={updateChildQuantity} value={childQuantity} />
          }
          sku={rowData.sku}
        />
      </tr>
    </>
  );
};

export default memo(SplitRow);

SplitRow.propTypes = {
  rowData: SplitShipRowDataPropType.isRequired,
};
